/**
 * Сторис — контракт ручек (схемы `Story`, `StoryInput`, `PublicStory` в
 * `openapi.yaml`, пока только на staging). Бэкенд сделан по этим типам и
 * docs/notes/stories-contract.md. Ошибка проверки — 400 `validation_failed`
 * с общим текстом, поэтому поля админка проверяет сама до отправки.
 *
 *   GET    /api/v1/stories                    — витрина: включённые и в периоде показа
 *   GET    /api/v1/admin/stories              — все (perm stories:r)
 *   POST   /api/v1/admin/stories              — создать (perm stories:c)
 *   GET    /api/v1/admin/stories/{id}         — одна (perm stories:r)
 *   PUT    /api/v1/admin/stories/{id}         — сохранить целиком, со слайдами (perm stories:u)
 *   DELETE /api/v1/admin/stories/{id}         — удалить (perm stories:d)
 *   POST   /api/v1/admin/stories/media        — загрузить картинку → { key, url } (perm stories:u)
 */

/** Один экран внутри сторис — картинка 9:16 и, если нужно, кнопка. */
export interface StorySlideDto {
  id: number
  /** Ключ файла из `POST /admin/stories/media`; нужен только для записи. */
  image_key: string
  /** Готовый адрес картинки от сервера. */
  image_url: string
  /** Сколько секунд слайд стоит на экране, STORY_DURATION_MIN…MAX. */
  duration_sec: number
  /** Подпись кнопки; пусто на обоих языках — кнопки нет. */
  button_ru: string
  button_uz: string
  /** Куда ведёт кнопка: путь приложения (`/catalog/12`) или `https://…`. */
  link: string
}

export interface StoryDto {
  id: number
  /** Подпись под кружком на главной — два-три слова. */
  title_ru: string
  title_uz: string
  /** Обложка в кружке; пустая строка — обложкой служит первый слайд. */
  cover_key: string
  cover_url: string
  slides: StorySlideDto[]
  /** Начало и конец показа, ISO 8601; null — без ограничения. */
  starts_at: string | null
  ends_at: string | null
  /** Порядок на главной: меньше — левее. */
  sort: number
  is_active: boolean
  /** Только в админке: сколько покупателей открыли сторис. */
  views?: number
  created_at: string
  updated_at: string
}

/** Слайд в запросе: id нет у нового слайда, адрес картинки сервер подставит сам. */
export interface StorySlideInput {
  id?: number
  image_key: string
  duration_sec: number
  button_ru: string
  button_uz: string
  link: string
}

export interface StoryInput {
  title_ru: string
  title_uz: string
  cover_key: string
  slides: StorySlideInput[]
  starts_at: string | null
  ends_at: string | null
  sort: number
  is_active: boolean
}

/** Ответ загрузки картинки. */
export interface StoryMediaDto {
  key: string
  url: string
}

export const STORY_SLIDES_MAX = 10
export const STORY_DURATION_MIN = 3
export const STORY_DURATION_MAX = 15
export const STORY_DURATION_DEFAULT = 5
export const STORY_TITLE_MAX = 40
export const STORY_BUTTON_MAX = 30
/** Самая большая картинка, которую примет сервер: 5 МБ, как у обложки категории. */
export const STORY_MEDIA_MAX_BYTES = 5 * 1024 * 1024

/** Слайд на витрине; у покупателя ровно эти поля. */
export type PublicStorySlideDto = Omit<StorySlideDto, 'image_key'>

/** Сторис на витрине: без служебных полей админки. */
export interface PublicStoryDto {
  id: number
  title_ru: string
  title_uz: string
  cover_url: string
  slides: PublicStorySlideDto[]
  /** Когда сторис менялась: изменённую покупатель увидит снова непросмотренной. */
  updated_at: string
}
